import { format } from "date-fns";
import { sv, enGB } from "date-fns/locale";
import { Info, User, Loader2, Pencil, Trash2, Check, X } from "lucide-react";
import { Comment } from "./types";
import { useTheme } from "@/context/ThemeContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getLanguage } from "@/utils/languageUtils";
import { leadUILabels } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useMemo, useState } from "react";

interface CommentListProps {
  comments: Comment[];
  customerComment?: {
    text: string;
    timestamp: Date;
  };
  noCommentsYet?: string;
  onUpdateComment?: (commentId: string, newText: string) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  compact?: boolean;
  dense?: boolean;
}

export const CommentList = ({
  comments,
  customerComment,
  noCommentsYet,
  onUpdateComment,
  onDeleteComment,
  compact = false,
  dense = false,
}: CommentListProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const language = getLanguage();
  const uiLabels = leadUILabels(language);
  const dateLocale = language === "en" ? enGB : sv;
  const defaultNoCommentsYet = uiLabels.noCommentsYet;

  const canEdit = useMemo(() => Boolean(onUpdateComment && onDeleteComment), [onUpdateComment, onDeleteComment]);
  const isTight = dense || compact;
  const iconBtnClass = dense ? "h-6 w-6" : undefined;
  const iconClass = dense ? "h-3 w-3" : "h-4 w-4";
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [deleteCandidateId, setDeleteCandidateId] = useState<string | null>(null);

  return (
    <div
      className={`overflow-y-auto h-full pr-0.5 ${dense ? "space-y-1" : isTight ? "space-y-1.5" : "space-y-3"}`}
    >
      {/* Customer comment at the top */}
      {customerComment && (
        <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
          <div
            className={`flex items-center gap-2 mb-2 ${
              isDark ? "text-blue-400" : "text-blue-700"
            }`}
          >
            <User className="h-4 w-4" />
            <span className="font-medium">{uiLabels.customerComment}</span>
          </div>
          <p
            className={`text-sm whitespace-pre-wrap ${isDark ? "text-white" : "text-black"}`}
          >
            {customerComment.text}
          </p>
          <div className="text-[11px] text-gray-500 mt-2">
            {format(customerComment.timestamp, "d MMM yyyy HH:mm", {
              locale: dateLocale,
            })}
          </div>
        </div>
      )}

      {/* Regular comments */}
      {comments.length === 0 && !customerComment ? (
        <div
          className={`text-center text-muted-foreground ${dense ? "py-3 text-xs" : "py-8"}`}
        >
          {noCommentsYet || defaultNoCommentsYet}
        </div>
      ) : (
        comments.map((comment) => (
          <div
            key={comment.id}
            className={
              dense
                ? "border rounded px-1.5 py-1 space-y-0.5"
                : compact
                  ? "border rounded-md px-2 py-1.5 space-y-1"
                  : "border rounded-lg px-3 py-2.5 space-y-2"
            }
          >
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-1 min-w-0">
                <span
                  className={`font-medium truncate ${dense ? "text-xs" : "text-sm"}`}
                >
                  {comment.author}
                </span>
                {comment.isPublic && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {uiLabels.publicComment}
                  </span>
                )}
                {comment.sourcedFromLead && (
                  <span className="text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2 py-0.5 rounded-full shrink-0">
                    {uiLabels.leadComment ?? "Lead-kommentar"}
                  </span>
                )}
                {comment.isLoading && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>{uiLabels.sending}</span>
                  </div>
                )}
                {comment.isFailed && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-red-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{uiLabels.failedToSendMessage}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <span
                  className={`leading-none text-muted-foreground ${dense ? "text-[10px]" : "text-[11px]"}`}
                >
                  {format(
                    comment.timestamp instanceof Date
                      ? comment.timestamp
                      : new Date(comment.timestamp),
                    dense ? "d MMM HH:mm" : "d MMM yyyy HH:mm",
                    { locale: dateLocale },
                  )}
                </span>
                {canEdit && !comment.isLoading && !comment.isFailed && (
                  <div className="flex items-center">
                    {editingId === comment.id ? (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={iconBtnClass}
                          disabled={isSaving || !draft.trim()}
                          onClick={async () => {
                            if (!onUpdateComment) return;
                            setIsSaving(true);
                            try {
                              await onUpdateComment(comment.id, draft.trim());
                              setEditingId(null);
                            } finally {
                              setIsSaving(false);
                            }
                          }}
                          aria-label={uiLabels.saveComment}
                          title={uiLabels.saveComment}
                        >
                          <Check className={iconClass} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={iconBtnClass}
                          disabled={isSaving}
                          onClick={() => {
                            setEditingId(null);
                            setDraft("");
                          }}
                          aria-label={uiLabels.cancelEdit}
                          title={uiLabels.cancelEdit}
                        >
                          <X className={iconClass} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={iconBtnClass}
                          onClick={() => {
                            setEditingId(comment.id);
                            setDraft(comment.text);
                          }}
                          aria-label={uiLabels.editComment}
                          title={uiLabels.editComment}
                        >
                          <Pencil className={iconClass} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={iconBtnClass}
                          onClick={() => setDeleteCandidateId(comment.id)}
                          aria-label={uiLabels.deleteComment}
                          title={uiLabels.deleteComment}
                        >
                          <Trash2 className={`${iconClass} text-red-600`} />
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            {editingId === comment.id ? (
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className={dense ? "min-h-[48px] text-xs py-1" : "min-h-[72px]"}
                autoFocus
              />
            ) : (
              <p
                className={`whitespace-pre-wrap ${dense ? "text-xs leading-snug" : "text-sm"}`}
              >
                {comment.text}
              </p>
            )}
            {comment.mentions && comment.mentions.length > 0 && (
              <div className="flex gap-1 text-sm text-blue-500">
                {comment.mentions.map((mention) => (
                  <span key={mention}>@{mention}</span>
                ))}
              </div>
            )}
          </div>
        ))
      )}

      <AlertDialog
        open={deleteCandidateId != null}
        onOpenChange={(open) => {
          if (!open) setDeleteCandidateId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{uiLabels.confirmDeleteCommentTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {uiLabels.confirmDeleteCommentDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>
              {uiLabels.cancelDeleteComment}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={isSaving}
              onClick={async () => {
                if (!onDeleteComment || !deleteCandidateId) return;
                setIsSaving(true);
                try {
                  await onDeleteComment(deleteCandidateId);
                } finally {
                  setIsSaving(false);
                  setDeleteCandidateId(null);
                }
              }}
            >
              {uiLabels.confirmDeleteComment}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
